// src/pages/Admin/VotePage/components/AudienceSelector.jsx

import React, { useMemo, useRef, useState } from "react";

import { FaCheck, FaLayerGroup, FaSearch, FaUsers } from "react-icons/fa";

import {
  getActorGroupLabel,
  getActorGroupMemberCount,
} from "../utils/voteAdmin.utils";

const cx = (...classes) => classes.filter(Boolean).join(" ");

/**
 * Recherche tous les parents possédant leur propre scroll.
 * Cela permet de conserver :
 * - le scroll interne de la liste des groupes ;
 * - le scroll du corps de la modale.
 */
const getScrollableAncestors = (element) => {
  if (typeof window === "undefined" || !element) {
    return [];
  }

  const scrollableAncestors = [];
  let currentElement = element.parentElement;

  while (currentElement && currentElement !== document.body) {
    const computedStyle = window.getComputedStyle(currentElement);

    const overflowY = computedStyle.overflowY;

    const isScrollable =
      (overflowY === "auto" || overflowY === "scroll") &&
      currentElement.scrollHeight > currentElement.clientHeight;

    if (isScrollable) {
      scrollableAncestors.push({
        element: currentElement,
        scrollTop: currentElement.scrollTop,
        scrollLeft: currentElement.scrollLeft,
      });
    }

    currentElement = currentElement.parentElement;
  }

  return scrollableAncestors;
};

/**
 * Exécute une modification de state tout en préservant
 * les positions de scroll des conteneurs parents.
 */
const runWithoutScrollJump = (sourceElement, callback) => {
  if (typeof window === "undefined") {
    callback();
    return;
  }

  const scrollableAncestors = getScrollableAncestors(sourceElement);

  const windowScrollX = window.scrollX;
  const windowScrollY = window.scrollY;

  callback();

  const restoreScrollPositions = () => {
    scrollableAncestors.forEach(({ element, scrollTop, scrollLeft }) => {
      element.scrollTop = scrollTop;
      element.scrollLeft = scrollLeft;
    });

    window.scrollTo(windowScrollX, windowScrollY);
  };

  /*
   * Deux frames sont utilisées :
   * - la première après la mise à jour React ;
   * - la seconde après le recalcul de la mise en page.
   */
  window.requestAnimationFrame(() => {
    restoreScrollPositions();

    window.requestAnimationFrame(() => {
      restoreScrollPositions();
    });
  });
};

export default function AudienceSelector({
  roleOptions = [],
  selectedRoleIds = [],
  selectedActorGroupIds = [],
  actorGroups = [],
  actorGroupsLoading = false,
  onToggleRole,
  onToggleActorGroup,
  onSelectAllRoles,
  onClearAudience,
}) {
  const [activeTab, setActiveTab] = useState("roles");

  const [groupSearch, setGroupSearch] = useState("");

  const groupListRef = useRef(null);

  const normalizedSelectedRoleIds = useMemo(
    () => selectedRoleIds.map(Number).filter(Number.isFinite),
    [selectedRoleIds],
  );

  const normalizedSelectedGroupIds = useMemo(
    () => selectedActorGroupIds.map(Number).filter(Number.isFinite),
    [selectedActorGroupIds],
  );

  const filteredGroups = useMemo(() => {
    const normalizedSearch = groupSearch.trim().toLowerCase();

    if (!normalizedSearch) {
      return actorGroups;
    }

    return actorGroups.filter((group) =>
      getActorGroupLabel(group).toLowerCase().includes(normalizedSearch),
    );
  }, [actorGroups, groupSearch]);

  const hasAudience =
    normalizedSelectedRoleIds.length > 0 ||
    normalizedSelectedGroupIds.length > 0;

  const handleRoleToggle = (event, roleId, selected) => {
    const buttonElement = event.currentTarget;

    runWithoutScrollJump(buttonElement, () => {
      onToggleRole?.(roleId, !selected);
    });
  };

  const handleGroupToggle = (event, groupId, selected) => {
    const buttonElement = event.currentTarget;

    /*
     * Sauvegarde supplémentaire du scroll interne
     * de la liste des groupes.
     */
    const currentGroupListScrollTop = groupListRef.current?.scrollTop ?? 0;

    runWithoutScrollJump(buttonElement, () => {
      onToggleActorGroup?.(groupId, !selected);
    });

    window.requestAnimationFrame(() => {
      if (groupListRef.current) {
        groupListRef.current.scrollTop = currentGroupListScrollTop;
      }

      window.requestAnimationFrame(() => {
        if (groupListRef.current) {
          groupListRef.current.scrollTop = currentGroupListScrollTop;
        }
      });
    });
  };

  const handleSelectAllRoles = (event) => {
    runWithoutScrollJump(event.currentTarget, () => {
      onSelectAllRoles?.(
        roleOptions.map((role) => Number(role.id)).filter(Number.isFinite),
      );
    });
  };

  const handleClearAudience = (event) => {
    runWithoutScrollJump(event.currentTarget, () => {
      onClearAudience?.();
    });
  };

  return (
    <section
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
      style={{
        overflowAnchor: "none",
      }}
    >
      <div className="border-b border-slate-200 bg-slate-50/80 p-4 dark:border-slate-700 dark:bg-slate-800/50">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="font-black text-slate-900 dark:text-white">
              Audience de la consultation
            </h3>

            <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              Les rôles et les groupes sont cumulés. Un utilisateur est autorisé
              s’il correspond à au moins une des audiences sélectionnées.
            </p>
          </div>

          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleClearAudience}
            disabled={!hasAudience}
            className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Audience générale
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 rounded-xl bg-slate-200/70 p-1 dark:bg-slate-950/70">
          <button
            type="button"
            onClick={() => setActiveTab("roles")}
            className={cx(
              "inline-flex h-10 items-center justify-center gap-2 rounded-lg px-3 text-xs font-black transition",
              activeTab === "roles"
                ? "bg-white text-indigo-700 shadow-sm dark:bg-slate-800 dark:text-indigo-300"
                : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white",
            )}
          >
            <FaUsers />
            Rôles
            {normalizedSelectedRoleIds.length > 0 && (
              <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
                {normalizedSelectedRoleIds.length}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("groups")}
            className={cx(
              "inline-flex h-10 items-center justify-center gap-2 rounded-lg px-3 text-xs font-black transition",
              activeTab === "groups"
                ? "bg-white text-violet-700 shadow-sm dark:bg-slate-800 dark:text-violet-300"
                : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white",
            )}
          >
            <FaLayerGroup />
            Groupes d’acteurs
            {normalizedSelectedGroupIds.length > 0 && (
              <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] text-violet-700 dark:bg-violet-500/20 dark:text-violet-300">
                {normalizedSelectedGroupIds.length}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="p-4">
        {activeTab === "roles" && (
          <>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="text-xs font-bold text-slate-500 dark:text-slate-400">
                Sélectionnez un ou plusieurs rôles.
              </div>

              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleSelectAllRoles}
                className="text-xs font-black text-indigo-600 hover:text-indigo-700 dark:text-indigo-300"
              >
                Sélectionner tous les rôles
              </button>
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              {roleOptions.map((role) => {
                const roleId = Number(role.id);

                const selected = normalizedSelectedRoleIds.includes(roleId);

                return (
                  <button
                    key={role.id}
                    type="button"
                    role="checkbox"
                    aria-checked={selected}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) =>
                      handleRoleToggle(event, roleId, selected)
                    }
                    className={cx(
                      "flex w-full items-center gap-3 rounded-xl border p-3 text-left transition",
                      selected
                        ? "border-indigo-300 bg-indigo-50 ring-2 ring-indigo-500/10 dark:border-indigo-500/40 dark:bg-indigo-500/10"
                        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800",
                    )}
                  >
                    <span
                      className={cx(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border text-xs transition",
                        selected
                          ? "border-indigo-600 bg-indigo-600 text-white"
                          : "border-slate-300 bg-white text-transparent dark:border-slate-600 dark:bg-slate-800",
                      )}
                    >
                      <FaCheck />
                    </span>

                    <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                      {role.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {activeTab === "groups" && (
          <>
            <div className="relative mb-3">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="search"
                value={groupSearch}
                onChange={(event) => setGroupSearch(event.target.value)}
                placeholder="Rechercher un groupe d’acteurs…"
                className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />
            </div>

            {actorGroupsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((item) => (
                  <div
                    key={item}
                    className="h-16 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800"
                  />
                ))}
              </div>
            ) : filteredGroups.length ? (
              <div
                ref={groupListRef}
                className="grid max-h-[320px] gap-2 overflow-y-auto overscroll-contain pr-1 sm:grid-cols-2"
                style={{
                  overflowAnchor: "none",
                  scrollbarGutter: "stable",
                }}
              >
                {filteredGroups.map((group) => {
                  const groupId = Number(group.id);

                  const selected = normalizedSelectedGroupIds.includes(groupId);

                  const actorCount = getActorGroupMemberCount(group);

                  return (
                    <button
                      key={group.id}
                      type="button"
                      role="checkbox"
                      aria-checked={selected}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) =>
                        handleGroupToggle(event, groupId, selected)
                      }
                      className={cx(
                        "flex min-h-[76px] w-full items-center gap-3 rounded-xl border p-3 text-left transition",
                        selected
                          ? "border-violet-300 bg-violet-50 ring-2 ring-violet-500/10 dark:border-violet-500/40 dark:bg-violet-500/10"
                          : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800",
                      )}
                    >
                      <span
                        className={cx(
                          "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border",
                          selected
                            ? "border-violet-600 bg-violet-600 text-white"
                            : "border-slate-300 bg-white text-slate-400 dark:border-slate-600 dark:bg-slate-800",
                        )}
                      >
                        {selected ? <FaCheck /> : <FaLayerGroup />}
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-black leading-snug text-slate-800 dark:text-slate-100">
                          {getActorGroupLabel(group)}
                        </span>

                        <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                          {actorCount} acteur
                          {actorCount > 1 ? "s" : ""}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-violet-200 bg-violet-50/60 p-6 text-center dark:border-violet-500/20 dark:bg-violet-500/5">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300">
                  <FaLayerGroup />
                </div>

                <div className="mt-3 text-sm font-black text-slate-700 dark:text-slate-200">
                  Aucun groupe trouvé
                </div>

                <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                  Aucun groupe ne correspond à votre recherche.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <footer className="border-t border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/40">
        {!hasAudience ? (
          <div className="flex items-center gap-2 text-xs font-bold text-emerald-700 dark:text-emerald-300">
            <FaUsers />
            Tous les utilisateurs pourront accéder à cette consultation.
          </div>
        ) : (
          <div className="text-xs font-bold text-slate-600 dark:text-slate-300">
            Audience ciblée : {normalizedSelectedRoleIds.length} rôle
            {normalizedSelectedRoleIds.length > 1 ? "s" : ""} et{" "}
            {normalizedSelectedGroupIds.length} groupe
            {normalizedSelectedGroupIds.length > 1 ? "s" : ""}.
          </div>
        )}
      </footer>
    </section>
  );
}
